import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import api from '../../api/api';
import { useStudentProgress } from '../../hooks/useStudentProgress';
import '../../styles/ChildAttendancePage.css';

const ChildAttendancePage = () => {
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [attendancePercentage, setAttendancePercentage] = useState('N/A');
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    present: 0,
    absent: 0,
    late: 0,
    total: 0
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { childId } = useParams();

  // Get childId from either params or location state
  const effectiveChildId = childId || location.state?.childId;
  const childName = location.state?.childName || 'Child';

  // Use the custom hook to get attendance data
  const { 
    loading: hookLoading, 
    error: hookError, 
    dashboardStats,
    attendanceData,
    refreshData
  } = useStudentProgress(effectiveChildId);

  useEffect(() => {
    if (!effectiveChildId) {
      setError('No child ID provided');
      setLoading(false);
      return;
    }
  }, [effectiveChildId]);

  // Process attendance data from hook when it's available
  useEffect(() => {
    if (hookLoading) {
      setLoading(true);
      return;
    }
    
    if (hookError) {
      setError(hookError);
      setLoading(false);
      return;
    }

    // Check if attendanceData exists and has records
    if (attendanceData) {
      console.log('Child attendance data from hook:', attendanceData);
      
      // If it's an array, it contains the attendance records
      if (Array.isArray(attendanceData)) {
        processAttendanceData(attendanceData);
      } 
      // If it has records property, use that
      else if (attendanceData.records && Array.isArray(attendanceData.records)) {
        processAttendanceData(attendanceData.records);
      }
      // No records found
      else {
        setAttendance([]);
      }
      
      // Process attendance stats from dashboard stats
      if (dashboardStats) {
        processAttendanceStats({
          attendanceRate: dashboardStats.attendanceRate,
          presentDays: attendanceData.stats?.presentDays || 0,
          absentDays: attendanceData.stats?.absentDays || 0,
          lateDays: attendanceData.stats?.lateDays || 0,
          totalDays: attendanceData.stats?.totalDays || 0
        });
      }
    }
    
    setLoading(false);
  }, [hookLoading, hookError, attendanceData, dashboardStats]);

  // Process attendance records
  const processAttendanceData = (records) => {
    // Process and format the attendance data
    const formattedAttendance = records.map(record => {
      // Format date
      const date = new Date(record.date);
      const formattedDate = date.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: 'numeric'
      });
        
      // Determine colors based on status
      let statusColor, backgroundColor, statusText;
        
      switch(record.status) {
        case 'present':
          statusColor = '#2e7d32';
          backgroundColor = '#e8f5e9';
          statusText = 'Present';
          break;
        case 'absent':
          statusColor = '#d32f2f';
          backgroundColor = '#ffebee';
          statusText = 'Absent';
          break;
        case 'late':
          statusColor = '#ff8f00';
          backgroundColor = '#fff8e1';
          statusText = 'Late';
          break;
        default:
          statusColor = '#757575';
          backgroundColor = '#f5f5f5';
          statusText = 'Unknown';
      }
        
      return {
        id: record._id,
        date: formattedDate,
        rawDate: date,
        status: record.status,
        statusText,
        statusColor,
        backgroundColor,
        halaqa: record.halaqaId ? (record.halaqaId.name || 'Unknown Class') : 'Unknown Class',
        markedBy: record.markedBy ? (record.markedBy.name || 'Unknown') : 'Unknown',
        comments: record.comments || '',
      };
    });
      
    // Sort by date (most recent first)
    formattedAttendance.sort((a, b) => b.rawDate - a.rawDate);
    
    setAttendance(formattedAttendance);
  };

  // Process attendance stats
  const processAttendanceStats = (stats) => {
    // Set attendance percentage
    if (stats.attendanceRate !== undefined) {
      setAttendancePercentage(`${stats.attendanceRate}%`);
    }
    
    // Set attendance stats
    setStats({
      present: stats.presentDays || 0,
      absent: stats.absentDays || 0,
      late: stats.lateDays || 0,
      total: stats.totalDays || 0
    });
  };

  const onRefresh = () => {
    setLoading(true);
    // Use the refreshData function provided by the hook
    if (typeof refreshData === 'function') {
      refreshData();
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading attendance history...</p>
      </div>
    );
  }

  return (
    <div className="child-attendance-page">
      <div className="page-header">
        <Link to={`/parent/children/${effectiveChildId}`} className="back-link">
          <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
          Back to Child Profile
        </Link>
        <h1>{childName}'s Attendance History</h1>
      </div>

      <div className="summary-container">
        <h2 className="summary-title">Attendance Summary</h2>
        
        <div className="percentage-container">
          <div className="percentage-value">{attendancePercentage}</div>
          <div className="percentage-label">Attendance Rate</div>
        </div>
        
        <div className="stats-row">
          <div className="stat-item present">
            <div className="stat-value">{attendance.filter(a => a.status === 'present').length}</div>
            <div className="stat-label">Present</div>
          </div>
          
          <div className="stat-item absent">
            <div className="stat-value">{attendance.filter(a => a.status === 'absent').length}</div>
            <div className="stat-label">Absent</div>
          </div>
          
          <div className="stat-item late">
            <div className="stat-value">{attendance.filter(a => a.status === 'late').length}</div>
            <div className="stat-label">Late</div>
          </div>
        </div>
      </div>

      <h2 className="section-title">Attendance History</h2>
      
      {error ? (
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button 
            className="retry-button"
            onClick={onRefresh}
          >
            Retry
          </button>
        </div>
      ) : attendance.length === 0 ? (
        <div className="empty-container">
          <p className="empty-text">No attendance records found</p>
        </div>
      ) : (
        <div className="attendance-list">
          {attendance.map((item) => (
            <div key={item.id} className="attendance-card">
              <div 
                className="status-indicator" 
                style={{ backgroundColor: item.statusColor }}
              ></div>
              
              <div className="attendance-content">
                <h3 className="date-text">{item.date}</h3>
                <div className="halaqa-text">{item.halaqa}</div>
                
                <div 
                  className="status-badge"
                  style={{ backgroundColor: item.backgroundColor }}
                >
                  <span style={{ color: item.statusColor }} className="status-text">
                    {item.statusText}
                  </span>
                </div>
                
                {item.comments && (
                  <div className="comments-text">
                    <span className="comments-label">Comments: </span>
                    {item.comments}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChildAttendancePage;